import { useState, useRef } from "react";
import { Upload, ImageIcon, X, Video, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TradingChartLoader } from "@/components/TradingChartLoader";
import api, { fileUrl, thumbUrl } from "@/lib/api";
import { uploadInChunks } from "@/lib/chunkedUpload";
import { toast } from "sonner";

/**
 * Reusable file uploader — images, videos or PDFs.
 *
 * Props:
 * - value: current file URL or id
 * - onChange: (newUrl) => void
 * - kind: "thumbnail" | "note" | "logo" | "video"
 * - label: field label
 * - accept: input accept attribute
 */
export function ThumbnailUploader({
  value = "",
  onChange,
  kind = "thumbnail",
  label = "File",
  accept,
  testId = "file-uploader",
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speedKbps, setSpeedKbps] = useState(0);
  const [urlInput, setUrlInput] = useState(value || "");
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);

  const isImage = kind === "thumbnail" || kind === "logo";
  const isVideo = kind === "video";
  const isPdf = kind === "note";

  const defaultAccept = isImage ? "image/*" : isVideo ? "video/*" : isPdf ? "application/pdf" : "*/*";
  const finalAccept = accept || defaultAccept;

  const formatBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setSpeedKbps(0);

    const controller = new AbortController();
    abortRef.current = controller;
    const startedAt = Date.now();
    let lastSent = 0;
    let lastT = startedAt;

    try {
      const data = await uploadInChunks(file, {
        kind,
        signal: controller.signal,
        // Higher concurrency for big files = faster upload
        concurrency: isVideo ? 4 : 3,
        onProgress: ({ loaded, total }) => {
          const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
          setProgress(pct);
          const now = Date.now();
          const dt = (now - lastT) / 1000;
          if (dt > 0.5) {
            const bytesPerSec = (loaded - lastSent) / dt;
            setSpeedKbps(Math.max(0, Math.round(bytesPerSec / 1024)));
            lastSent = loaded;
            lastT = now;
          }
        },
      });
      const url = fileUrl(data.id);
      setUrlInput(url);
      onChange(url);
      toast.success(`File uploaded successfully (${formatBytes(data.size || file.size)}).`);
    } catch (err) {
      if (controller.signal.aborted) {
        toast.message("Upload cancelled.");
      } else {
        const msg = err?.response?.data?.detail || err?.message || "Failed to upload the file.";
        toast.error(typeof msg === "string" ? msg : "Failed to upload the file.");
      }
    } finally {
      setUploading(false);
      setProgress(0);
      setSpeedKbps(0);
      abortRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const cancelUpload = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleUrlChange = (e) => {
    setUrlInput(e.target.value);
    onChange(e.target.value);
  };

  const clear = () => {
    setUrlInput("");
    onChange("");
  };

  const previewUrl = urlInput || value;
  const FileIcon = isPdf ? FileText : isVideo ? Video : ImageIcon;

  const formatSpeed = () => {
    if (!speedKbps) return "";
    if (speedKbps < 1024) return `${speedKbps} KB/s`;
    return `${(speedKbps / 1024).toFixed(1)} MB/s`;
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <Label className="text-sm font-medium">{label}</Label>

      {/* Preview */}
      <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-border bg-muted/30">
        {previewUrl && isImage ? (
          <img
            src={thumbUrl(previewUrl)}
            alt="Preview"
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            data-testid="file-preview-img"
          />
        ) : previewUrl && isVideo ? (
          <video
            src={previewUrl}
            controls
            className="absolute inset-0 h-full w-full object-contain bg-black"
            data-testid="file-preview-video"
          />
        ) : previewUrl && isPdf ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground p-4">
            <FileText className="h-10 w-10 mb-2" strokeWidth={1.5} />
            <p className="text-xs break-all text-center">{previewUrl}</p>
            <a href={previewUrl} target="_blank" rel="noreferrer" className="mt-2 text-xs text-primary underline">Open PDF</a>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
            <FileIcon className="h-8 w-8 mb-2" strokeWidth={1.5} />
            <p className="text-sm">{isVideo ? "Video not uploaded" : isPdf ? "PDF not uploaded" : "Thumbnail not uploaded"}</p>
          </div>
        )}

        {previewUrl && !uploading && (
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-7 w-7"
            onClick={clear}
            data-testid="clear-file-btn"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3 p-4">
            <TradingChartLoader size="md" text={`Uploading ${progress}%`} />
            <div className="w-full max-w-xs space-y-1">
              <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-teal-400 to-teal-300 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              {speedKbps > 0 && (
                <p className="text-[11px] text-white/70 text-center">{formatSpeed()}</p>
              )}
            </div>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={cancelUpload}
              data-testid="cancel-upload-btn"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* URL input */}
      <Input
        type="text"
        placeholder={isVideo ? "https://...video.mp4 or YouTube URL" : isPdf ? "https://...file.pdf" : "https://...image.jpg"}
        value={urlInput}
        onChange={handleUrlChange}
        data-testid="file-url-input"
      />

      {/* Upload button */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex-1 sm:flex-none"
          data-testid="file-upload-btn"
        >
          <Upload className="h-4 w-4" />
          {uploading ? `Uploading ${progress}%` : "Choose File from PC"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={finalAccept}
          className="hidden"
          onChange={handleFile}
        />
        <p className="text-xs text-muted-foreground">
          {isVideo
            ? "MP4/WebM/MOV, up to 10GB · ultra-fast parallel upload"
            : isPdf
              ? "PDF, up to 200MB"
              : "JPG/PNG/WebP, up to 50MB · 1280×720 recommended"}
        </p>
      </div>
    </div>
  );
}

